import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { operatorService } from '../../services/operator.service';
import type { Operator, OperatorInput, OperatorStatus, OperatorType } from '../../types/crm';
import Loading from '../../components/Loading';

const OPERATOR_TYPES: OperatorType[] = ['restaurant', 'hotel', 'healthcare', 'education', 'catering', 'other'];
const OPERATOR_STATUSES: OperatorStatus[] = ['prospect', 'active', 'inactive', 'lost'];

const createOperatorSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  operator_type: z.enum(['restaurant', 'hotel', 'healthcare', 'education', 'catering', 'other']),
  status: z.enum(['prospect', 'active', 'inactive', 'lost']),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
});

type CreateOperatorFormData = z.infer<typeof createOperatorSchema>;

const titleize = (value: string): string => value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const Operators = () => {
  const [operators, setOperators] = useState<Operator[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<OperatorType | ''>('');
  const [statusFilter, setStatusFilter] = useState<OperatorStatus | ''>('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateOperatorFormData>({
    resolver: zodResolver(createOperatorSchema),
    defaultValues: {
      operator_type: 'restaurant',
      status: 'prospect',
    },
  });

  useEffect(() => {
    loadOperators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, typeFilter, statusFilter]);

  const loadOperators = async () => {
    setIsLoading(true);
    try {
      const params: Record<string, unknown> = {};
      if (search) params['q[name_cont]'] = search;
      if (typeFilter) params['q[operator_type_eq]'] = typeFilter;
      if (statusFilter) params['q[status_eq]'] = statusFilter;
      const result = await operatorService.getAll(params);
      setOperators(result.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const onCreate = async (data: CreateOperatorFormData) => {
    setIsCreating(true);
    try {
      const input: OperatorInput = {
        name: data.name,
        operator_type: data.operator_type,
        status: data.status,
        email: data.email || null,
        phone: data.phone || null,
        city: data.city || null,
        state: data.state || null,
      };
      await operatorService.create(input);
      toast.success('Operator created');
      setShowCreateModal(false);
      reset();
      loadOperators();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Operators</h1>
          <p className="text-sm text-gray-600 mt-1">Restaurants, hotels, and other operator accounts.</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
          <Plus className="h-4 w-4 inline mr-2" />
          Add Operator
        </button>
      </div>

      <div className="card p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="label">Search</label>
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input"
          />
        </div>
        <div>
          <label className="label">Type</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as OperatorType | '')} className="input">
            <option value="">All types</option>
            {OPERATOR_TYPES.map((t) => (
              <option key={t} value={t}>{titleize(t)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as OperatorStatus | '')} className="input">
            <option value="">All statuses</option>
            {OPERATOR_STATUSES.map((s) => (
              <option key={s} value={s}>{titleize(s)}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="p-8"><Loading /></div>
        ) : operators.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No operators found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {operators.map((op) => (
                  <tr key={op.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-800">
                      <Link to={`/crm/operators/${op.id}`}>{op.name}</Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{titleize(op.operator_type)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        op.status === 'active' ? 'bg-green-100 text-green-800' :
                        op.status === 'prospect' ? 'bg-yellow-100 text-yellow-800' :
                        op.status === 'lost' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {titleize(op.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {[op.city, op.state].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{op.email || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowCreateModal(false)} />
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit(onCreate)}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Add New Operator</h3>
                    <button type="button" onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-500">
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="label">Name</label>
                      <input {...register('name')} id="name" type="text" className="input" />
                      {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="operator_type" className="label">Type</label>
                        <select {...register('operator_type')} id="operator_type" className="input">
                          {OPERATOR_TYPES.map((t) => (
                            <option key={t} value={t}>{titleize(t)}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="status" className="label">Status</label>
                        <select {...register('status')} id="status" className="input">
                          {OPERATOR_STATUSES.map((s) => (
                            <option key={s} value={s}>{titleize(s)}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="label">Email</label>
                      <input {...register('email')} id="email" type="email" className="input" />
                      {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="phone" className="label">Phone</label>
                      <input {...register('phone')} id="phone" type="text" className="input" />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="city" className="label">City</label>
                        <input {...register('city')} id="city" type="text" className="input" />
                      </div>
                      <div>
                        <label htmlFor="state" className="label">State</label>
                        <input {...register('state')} id="state" type="text" className="input" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button type="submit" disabled={isCreating} className="btn btn-primary">
                    {isCreating ? 'Creating...' : 'Create Operator'}
                  </button>
                  <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Operators;
